import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import logo from "@/assets/logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { capture } from "@/instrument";
import { HiOutlineTrash, HiOutlineDocumentAdd, HiChevronDown, HiOutlineCloudUpload, HiChevronUp, HiCheckCircle } from "react-icons/hi";
import RenewalsModal from "@/components/RenewalsModal";
import { groupRenewalsByPrescription } from "@/utils";

const faqItems = [
  {
    question: "Je me suis déjà pré-enregistré en ligne, qu'est-ce que je dois faire ?",
    answer: "Consultez vos emails et cliquez sur le bouton 'Signaler ma présence' et scannez le QR code affiché dans votre laboratoire",
  },
  {
    question: "Quels documents puis-je envoyer ?",
    answer: "Vous pouvez envoyer votre ordonnance médicale au format JPG, PNG ou PDF. Assurez-vous que le document est lisible et que toutes les informations sont visibles.",
  },
  {
    question: "Dois-je apporter mon ordonnance physique au laboratoire ?",
    answer: "Oui, même après l'enregistrement en ligne, vous devez présenter votre ordonnance originale lors de votre visite au laboratoire.",
  },
  {
    question: "Combien de temps prend l'enregistrement ?",
    answer:
      "L'analyse de votre ordonnance prend quelques secondes seulement. Ensuite, il vous suffira de remplir vos informations personnelles, ce qui prend généralement 3 à 5 minutes.",
  },
  {
    question: "Mes données sont-elles sécurisées ?",
    answer: "Oui, toutes vos données sont chiffrées et sécurisées. Nous respectons strictement le RGPD et le secret médical.",
  },
  {
    question: "Puis-je m'enregistrer pour quelqu'un d'autre ?",
    answer: "Oui, vous pouvez enregistrer pour quelqu'un d'autre mais vous devez avoir les autorisations de celui-ci.",
  },
  {
    question: "Que faire si mon ordonnance n'est pas lisible ?",
    answer:
      "Si votre ordonnance n'est pas lisible, essayez de prendre une nouvelle photo avec un bon éclairage, sans reflet ni zone d'ombre. Si le problème persiste, présentez-vous directement au laboratoire.",
  },
];

const Prescription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileDevice = useMemo(() => /Mobi|Android/i.test(navigator.userAgent), []);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);

  const [prescriptions, setPrescriptions] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [laboratories, setLaboratories] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hasAcknowledgedEdgeCases, setHasAcknowledgedEdgeCases] = useState(false);
  const [showEdgeCaseModal, setShowEdgeCaseModal] = useState(true);
  const [showRenewalsModal, setShowRenewalsModal] = useState(false);
  const [patient, setPatient] = useState(null);
  const [isNotEligible, setIsNotEligible] = useState(false);
  const [expandedFaqIndex, setExpandedFaqIndex] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const laboratoryFromQuery = queryParams.get("laboratory");
  // const laboratoryToken = queryParams.get("laboratoryToken"); // TODO: Remove this when the system is working
  const laboratoryToken = undefined;
  const [selectedLaboratory, setSelectedLaboratory] = useState(laboratoryFromQuery || null);

  const visibleLaboratories = useMemo(() => laboratories.filter((l) => l.isVisible !== false), [laboratories]);

  const selectedLaboratoryData = useMemo(() => {
    if (!selectedLaboratory) return null;
    for (let i = 0; i < laboratories.length; i++) {
      const laboratory = laboratories[i];
      if (laboratory._id === selectedLaboratory) return laboratory;
    }
    return null;
  }, [laboratories, selectedLaboratory]);

  const homePageCustomMessage = selectedLaboratoryData && typeof selectedLaboratoryData.homePageMessage === "string" ? selectedLaboratoryData.homePageMessage.trim() : "";
  const renewablePrescriptionsCount = groupRenewalsByPrescription(patient?.renewals || []).length;

  const filePicker = useRef();
  const dropAreaRef = useRef();

  useEffect(() => {
    getLaboratories();
    checkRenewals();
  }, []);

  const checkRenewals = async () => {
    try {
      const res = await api.get(`/patient/me`);
      if (res.ok && res.data?.renewals?.length > 0) setPatient(res.data);
    } catch (_) {}
  };

  const getLaboratories = async () => {
    try {
      const res = await api.get("/laboratory");
      if (!res.ok) throw res;
      setLaboratories(res.data);

      if (res.data.find((laboratory) => laboratory._id === laboratoryFromQuery)) return;
      if (laboratoryFromQuery) toast.error("Ce laboratoire n'est pas disponible.");
      const firstVisible = res.data.find((laboratory) => laboratory.isVisible !== false) || res.data[0];
      setSelectedLaboratory(firstVisible._id);
    } catch (error) {
      capture(error);
      toast.error("Impossible de charger les laboratoires. Réessayez.");
    }
  };

  const processFiles = (files) => {
    const newFiles = Array.from(files);
    const acceptedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];

    const validFiles = newFiles.filter((file) => acceptedTypes.includes(file.type));

    if (validFiles.length !== newFiles.length) {
      toast.error("Certains fichiers sont invalides. Formats acceptés : JPG, PNG, PDF");
    }

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileType = file.type === "application/pdf" ? "pdf" : "image";
      const objectUrl = URL.createObjectURL(file);
      setPreviews((prev) => [...prev, { type: fileType, url: objectUrl }]);
    }

    setPrescriptions((prev) => [...prev, ...validFiles]);
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const removePrescription = (index) => {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setPrescriptions((prev) => prev.filter((_, i) => i !== index));
  };

  const scrollToFirstInvalidField = () => {
    if (!prescriptions.length) {
      const dropArea = dropAreaRef.current;
      if (dropArea) return dropArea.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const sendPrescriptions = async () => {
    setSubmissionAttempted(true);

    let interval;
    try {
      if (!prescriptions.length) {
        scrollToFirstInvalidField();
        toast.error("Ajoutez votre ordonnance");
        return;
      }

      setLoading(true);

      interval = setInterval(() => {
        setProgress((oldProgress) => (oldProgress >= 95 ? clearInterval(interval) : oldProgress + 0.5));
      }, 50);

      const formData = new FormData();
      for (let i = 0; i < prescriptions.length; i++) {
        formData.append("files", prescriptions[i]);
      }
      formData.append("laboratoryId", selectedLaboratory);
      if (laboratoryToken) formData.append("type", "NOW");

      const res = await api.postFormData("/admission", formData);

      setProgress(100);
      clearInterval(interval);

      if (!res.ok) throw res;

      if (res.auth) {
        sessionStorage.setItem("formToken", res.auth.formToken);
        sessionStorage.setItem("deviceId", res.auth.deviceId);
      }

      if (laboratoryToken && res?.isEligible === false) {
        setLoadingStatus("error");
        setIsNotEligible(true);
        return;
      }

      setLoadingStatus("success");
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return navigate(`/identification`);
    } catch (error) {
      clearInterval(interval);
      setProgress(100);
      setLoadingStatus("error");

      await new Promise((resolve) => setTimeout(resolve, 1200));

      setLoading(false);
      setLoadingStatus(null);
      setPrescriptions([]);
      setPreviews([]);
      setProgress(0);

      if (error.code === "NO_VALID_CONTENT_FOUND") {
        toast.error("Votre ordonnance n'est pas lisible. Réessayez avec une autre photo.");
      } else {
        capture(error);
        toast.error("Une erreur est survenue. Réessayez.");
      }
    }
  };

  const getDropZoneClasses = () => {
    let baseClasses = "flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-8 transition-all duration-200 cursor-pointer min-h-[200px]";

    if (isDragging) return `${baseClasses} border-primary bg-primary-50`;
    if (submissionAttempted && !prescriptions.length) return `${baseClasses} border-red-400 bg-red-50`;
    return `${baseClasses} border-gray-300 bg-white hover:border-primary hover:bg-primary-50/50`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <img src={logo} alt="Logo" className="h-8 pr-4" />
            <div className="relative">
              <select
                id="laboratory-select"
                className="appearance-none w-full bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer truncate disabled:opacity-50 disabled:cursor-not-allowed"
                onChange={(e) => setSelectedLaboratory(e.target.value)}
                value={selectedLaboratory}
                aria-label="Sélectionner un laboratoire"
                disabled={loading}
              >
                {visibleLaboratories.map((laboratory) => (
                  <option key={laboratory._id} value={laboratory._id} className="truncate">
                    {laboratory.name}
                  </option>
                ))}
                {selectedLaboratoryData && selectedLaboratoryData.isVisible === false && (
                  <option key={selectedLaboratoryData._id} value={selectedLaboratoryData._id} className="truncate">
                    {selectedLaboratoryData.name}
                  </option>
                )}
              </select>
              <HiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-56 md:grid md:grid-cols-2 md:gap-4 md:max-w-7xl items-start">
        <div className={`animate-fade-in ${loading ? "hidden" : ""}`}>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Bienvenue {patient?.firstName} {patient?.lastName}
            </h1>
            {homePageCustomMessage.length > 0 ? (
              <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-gray-800 whitespace-pre-wrap text-[15px] leading-relaxed">
                {homePageCustomMessage}
              </div>
            ) : (
              <p className="text-gray-600">
                {laboratoryToken
                  ? "Nous allons vérifier si vous pouvez vous enregistrer directement en ligne."
                  : "Enregistrez-vous en ligne maintenant pour gagner du temps lors de votre visite au laboratoire."}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
            {prescriptions.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    {prescriptions.length} document{prescriptions.length > 1 ? "s" : ""} ajouté{prescriptions.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                  {prescriptions.map((file, index) => (
                    <div key={index} className="relative flex-shrink-0 w-32 bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                      <div className="aspect-[3/4] w-full">
                        {previews[index]?.type === "pdf" ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <HiOutlineDocumentAdd className="w-8 h-8 text-gray-400" />
                          </div>
                        ) : (
                          <img src={previews[index]?.url} alt={`Ordonnance ${index + 1}`} className="object-cover w-full h-full" />
                        )}
                      </div>
                      <button
                        onClick={() => removePrescription(index)}
                        className="absolute top-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-600 hover:text-red-500 hover:bg-white shadow-sm transition-colors"
                        aria-label="Supprimer"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm px-2 py-1.5">
                        <p className="text-xs text-gray-600 truncate">{file.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prescriptions.length < 2 && (
              <>
                <input ref={filePicker} accept="image/png,image/jpeg,image/jpg,application/pdf" type="file" hidden multiple onChange={handleFileChange} />
                <div
                  ref={dropAreaRef}
                  className={getDropZoneClasses()}
                  onClick={() => filePicker.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDragging ? "bg-primary-100" : "bg-gray-100"}`}>
                    <HiOutlineCloudUpload className={`w-8 h-8 ${isDragging ? "text-primary" : "text-gray-400"}`} />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1 text-center">{isMobileDevice ? "Prendre une photo de votre ordonnance" : "Déposez votre ordonnance ici"}</p>
                  <p className="text-sm text-gray-500 mb-3">{isMobileDevice ? "ou choisir dans vos fichiers" : "ou cliquez pour choisir un fichier"}</p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-600">JPG, PNG, PDF</span>
                </div>
              </>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in col-span-2">
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 transition-all duration-500 ${
                loadingStatus === "success" ? "bg-green-100 animate-success-pulse" : loadingStatus === "error" ? "bg-red-100 animate-error-shake" : "bg-primary-50"
              }`}
            >
              {loadingStatus === null && <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />}
              {loadingStatus === "success" && (
                <svg className="w-10 h-10 animate-check-appear" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#22c55e"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-draw-check"
                    style={{ strokeDasharray: "50", strokeDashoffset: "50" }}
                  />
                </svg>
              )}
              {loadingStatus === "error" && (
                <svg className="w-10 h-10 animate-cross-appear" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M18 6l-12 12"
                    stroke="#ef4444"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-draw-cross"
                    style={{ strokeDasharray: "30", strokeDashoffset: "30" }}
                  />
                </svg>
              )}
            </div>
            <p
              className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                loadingStatus === "success" ? "text-green-600" : loadingStatus === "error" ? "text-red-600" : "text-gray-900"
              }`}
            >
              {loadingStatus === "success"
                ? "C'est bon !"
                : loadingStatus === "error" && isNotEligible
                  ? "Pas d'enregistrement en ligne"
                  : loadingStatus === "error"
                    ? "Une erreur est survenue"
                    : laboratoryToken
                      ? "Vérification en cours..."
                      : "Analyse en cours..."}
            </p>
            <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
              {loadingStatus === "success"
                ? "Vous allez être redirigé..."
                : loadingStatus === "error" && isNotEligible
                  ? "Pour faire votre analyse, merci de vous présenter directement au secrétariat du laboratoire."
                  : loadingStatus === "error"
                    ? "Veuillez réessayer"
                    : "Quelques secondes seulement"}
            </p>
            {loadingStatus === "error" && isNotEligible ? (
              <button
                onClick={() => {
                  setLoading(false);
                  setLoadingStatus(null);
                  setIsNotEligible(false);
                  setPrescriptions([]);
                  setPreviews([]);
                  setProgress(0);
                }}
                className="w-full max-w-xs py-3.5 px-6 bg-red-500 text-white font-semibold rounded-xl shadow-soft hover:bg-red-600 transition-colors min-touch-target"
              >
                Compris
              </button>
            ) : (
              <div className="w-full max-w-xs">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${progress}%` }}
                    className={`h-full rounded-full transition-all duration-100 ${
                      loadingStatus === "success" ? "bg-green-500" : loadingStatus === "error" ? "bg-red-500" : "bg-primary"
                    }`}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">{Math.round(progress) || 99}%</p>
              </div>
            )}
          </div>
        )}

        {!loading && (
          <div className="mt-8 md:mt-0 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Questions fréquentes</h2>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedFaqIndex(expandedFaqIndex === index ? null : index)}
                      className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900 pr-4">{item.question}</span>
                      {expandedFaqIndex === index ? (
                        <HiChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <HiChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {expandedFaqIndex === index && (
                      <div className="px-4 pb-4 pt-1">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {!loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100">
          <div className="max-w-lg mx-auto px-4 py-4 safe-bottom">
            <div className="space-y-3">
              <button
                className="w-full px-4 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-600 transition-colors min-touch-target"
                onClick={sendPrescriptions}
              >
                {laboratoryToken ? <span>Vérifier votre éligibilité</span> : <span>Continuer avec {prescriptions.length > 1 ? "ces ordonnances" : "cette ordonnance"}</span>}
              </button>
              {patient?.renewals?.length > 0 ? (
                <div className="relative">
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={showRenewalsModal === true}
                    aria-controls="renewalsModal"
                    onClick={() => setShowRenewalsModal(true)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 min-touch-target"
                  >
                    <span>Utiliser {renewablePrescriptionsCount > 1 ? "mes ordonnances renouvelables" : "mon ordonnance renouvelable"}</span>
                  </button>
                  {renewablePrescriptionsCount > 1 && (
                    <div className="absolute -right-1.5 -top-1.5 bg-primary text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {renewablePrescriptionsCount}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  className="w-full px-4 py-3.5 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 min-touch-target"
                  onClick={() => setShowRenewalsModal(true)}
                >
                  Réutiliser une ordonnance renouvelable
                </button>
              )}
            </div>
            <p className="mt-3 text-xs text-gray-400 text-center">
              En continuant, vous acceptez nos{" "}
              <a href="/terms" className="text-primary hover:underline">
                conditions d'utilisation
              </a>
              . Consultez notre{" "}
              <a href="/privacy" className="text-primary hover:underline">
                politique de confidentialité
              </a>{" "}
              et gérez vos{" "}
              <a href="/cookies" className="text-primary hover:underline">
                cookies
              </a>
              .
            </p>
          </div>
        </div>
      )}

      {showEdgeCaseModal === true && !hasAcknowledgedEdgeCases && (
        <div id="edgeCaseModal" role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" />
          <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-elevated animate-scale-in overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Avant de commencer</h2>
              <p className="text-sm text-gray-600 mb-5">Ce service en ligne est disponible si :</p>

              <ul role="list" className="space-y-3.5 mb-5">
                <li className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <p className="text-base text-gray-900">
                    Vous êtes <span className="font-semibold">majeur</span>
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <p className="text-base text-gray-900">
                    Vous avez votre <span className="font-semibold">carte vitale</span>
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <HiCheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-base text-gray-900">
                      Vous disposez d'une <span className="font-semibold">ordonnance</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">Hors préfecture, médecine du travail, assurance</p>
                  </div>
                </li>
              </ul>

              <p className="text-sm text-gray-600 mb-5">
                Dans tous les autres cas, présentez-vous directement à <span className="font-medium text-gray-900">l'accueil du laboratoire</span>.
              </p>

              <button
                onClick={() => {
                  setHasAcknowledgedEdgeCases(true);
                  setShowEdgeCaseModal(false);
                }}
                className="w-full px-4 py-3 rounded-xl bg-primary text-white font-medium hover:bg-primary-600 transition-colors"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {showRenewalsModal && (
        <RenewalsModal laboratoryId={selectedLaboratory} patient={patient} onClose={() => setShowRenewalsModal(false)} type={laboratoryToken ? "NOW" : "LATER"} />
      )}
    </div>
  );
};

export default Prescription;
